import { useMemo } from 'react';
import { useUrlState } from '#client/hooks/useUrlState';
import { dateKey } from '#client/lib/dates';

/**
 * The from/to pair every finance screen is driven by, held in the query string.
 *
 * In the URL for the same reason the worklist's filters are: "look at last
 * month" has to be a link somebody can paste into a message, and it has to
 * survive a refresh and step properly under Back.
 *
 * The default is **this calendar month**, not the last thirty days. A lab owner
 * asking "how did we do" means a month boundary — that is what the rent and the
 * salaries land on — and a rolling window silently double-counts a cost that
 * falls in the overlap.
 */
export interface DateRange {
  from: string;
  to: string;
  setRange: (from: string, to: string) => void;
  /** True when the range is exactly the current calendar month. */
  isThisMonth: boolean;
}

export function useDateRange(): DateRange {
  const { params, update } = useUrlState();

  const defaults = useMemo(() => monthToDate(), []);
  const from = params.get('from') ?? defaults.from;
  const to = params.get('to') ?? defaults.to;

  const setRange = (nextFrom: string, nextTo: string) =>
    update((next) => {
      next.set('from', nextFrom);
      next.set('to', nextTo);
    });

  return {
    from,
    to,
    setRange,
    isThisMonth: from === defaults.from && to === defaults.to,
  };
}

/**
 * The first of this month to today, in the **browser's** local calendar.
 *
 * That matches the lab's timezone in practice — the staff are in the same place
 * as the server — and `toISOString()` would not: it converts to UTC first, so on
 * the 1st of a month at 9 a.m. PKT it returns the *previous* month's last day.
 */
function monthToDate(): { from: string; to: string } {
  const now = new Date();
  return { from: dateKey(new Date(now.getFullYear(), now.getMonth(), 1)), to: dateKey(now) };
}

/** A named period, for the quick-pick buttons. */
interface Preset {
  label: string;
  range: () => { from: string; to: string };
}

export const PRESETS: readonly Preset[] = [
  {
    label: 'This month',
    range: monthToDate,
  },
  {
    label: 'Last month',
    range: () => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      // Day 0 of the following month is the last day of this one, which handles
      // February and the 31-day months without a table.
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: dateKey(first), to: dateKey(last) };
    },
  },
  {
    label: 'This year',
    range: () => {
      const now = new Date();
      return { from: dateKey(new Date(now.getFullYear(), 0, 1)), to: dateKey(now) };
    },
  },
];
